import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import { MASTER_SCHEDULE_PEOPLE_ROTATION_QUERY_KEY } from "../pages/MasterSchedule/v3/hooks";
import { invalidatePbjPredictionJobCoverage } from "../pages/PbjModel/hooks/pbjQueryOptions";
import { UPCOMING_TURNOVER_QUERY_KEY } from "../pages/PbjModel/hooks/usePbjModel";
import { backendService } from "../services/backend.service";
import { showAgencyWarnings } from "../utils/agencyWarnings";
import type { AgencyPostingFailure, PublishRequestBody } from "../utils/models";
import { NURSE_SCHEDULE_QUERY_KEY } from "./useInvalidateNurseQueries";
import { TEAM_STATS_QUERY_KEY } from "./useInvalidateTeamQueries";

type CancelledPto = { nurseId: number; preferenceId: string };

export const usePublishChanges = () => {
	const { chosenDepartment, getNurseById, updateNurse } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (requestBody: PublishRequestBody) => {
			const response = await backendService.publish(chosenDepartment.department_id, requestBody);

			// Scheduling onto a nurse's PTO cancels that PTO server-side (DAT-156). refreshDepartmentData
			// doesn't reload nurse preferences, so drop the cancelled prefs locally — same as the staff-table
			// delete-PTO flow — to clear the PTO from the grid without a full refresh. Done here in the
			// shared publish mutation so every add path (weekly grid, day-view quick add) is covered.
			const cancelledPto: CancelledPto[] = response.data?.cancelledPto ?? [];
			if (cancelledPto.length > 0) {
				for (const { nurseId, preferenceId } of cancelledPto) {
					const nurse = getNurseById(nurseId);
					if (nurse) {
						updateNurse({ ...nurse, employeeSpecificPreferences: nurse.employeeSpecificPreferences.filter((pref) => pref.id !== preferenceId) });
					}
				}
				showSnackbar(t("pto.cancelled.on.assignment"), "info");
			}

			// FF off → BE returns a per-agency reminder to remove the nurse on the agency manually.
			const agencyPostingFailures: AgencyPostingFailure[] = response.data?.agency_posting_failures ?? [];
			showAgencyWarnings(showSnackbar, t, agencyPostingFailures);

			return response.data;
		},
		// The PBJ Upcoming Turnover table's "Have Schedule Shift" column reads the schedule (`nurse_in_shift`),
		// so any published shift add/remove can change it. Publishing is the only path that writes a
		// non-pending shift — draft edits stay `is_pending` and are excluded from the column by design — so
		// invalidating here (rather than on each draft change) marks the cached rows stale exactly when the
		// underlying data moved. The tab is usually unmounted at this point, which is the reason this is
		// needed at all: without it the hour-long `staleTime` served the pre-publish snapshot on next visit.
		onSuccess: (_data, requestBody) => {
			queryClient.invalidateQueries({ queryKey: UPCOMING_TURNOVER_QUERY_KEY });
			// The Staffing People roster is a cached query, not HospitalDataProvider state, so a publish that
			// adds or removes a shift does not re-render it. Invalidating by key prefix covers every
			// department/week/start-day variant currently cached.
			queryClient.invalidateQueries({ queryKey: [MASTER_SCHEDULE_PEOPLE_ROTATION_QUERY_KEY] });
			// Same for the profile grid's per-nurse weeks: every published add, edit or removal lands here.
			queryClient.invalidateQueries({ queryKey: NURSE_SCHEDULE_QUERY_KEY });
			// And for the Team page's Worked Last Month and Recurring Shifts Staff cards, which count these shifts.
			queryClient.invalidateQueries({ queryKey: TEAM_STATS_QUERY_KEY });
			if (requestBody.actual_changes.length > 0) {
				invalidatePbjPredictionJobCoverage(queryClient);
			}
		},
	});
};
